<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import type { SharedData } from '@/types'
import { router, usePage } from '@inertiajs/vue3'
import { useColorMode } from '@vueuse/core'

import { computed } from 'vue'
import { adminSettingsProfilePath, sessionPath } from '@/routes'

defineProps<{
  collapsed?: boolean
}>()

const page = usePage()
const colorMode = useColorMode()

const auth = computed(() => page.props.auth as SharedData['auth'])
const user = computed(() => auth.value.user)
const avatar = computed(() => ({
  alt: user.value.name,
  src: user.value.avatar || undefined,
}))

function logout() {
  router.flushAll()
  router.delete(sessionPath({ id: auth.value.session.id }))
}

const items = computed<DropdownMenuItem[][]>(() => [
  [
    {
      type: 'label',
      label: user.value.name,
      avatar: avatar.value,
    },
  ],
  [
    {
      label: 'Profile',
      icon: 'i-lucide-user',
      to: adminSettingsProfilePath(),
    },
  ],
  [
    {
      label: 'Appearance',
      icon: 'i-lucide-sun-moon',
      children: [
        {
          label: 'Light',
          icon: 'i-lucide-sun',
          type: 'checkbox',
          checked: colorMode.value === 'light',
          onSelect(event: Event) {
            event.preventDefault()
            colorMode.value = 'light'
          },
        },
        {
          label: 'Dark',
          icon: 'i-lucide-moon',
          type: 'checkbox',
          checked: colorMode.value === 'dark',
          onSelect(event: Event) {
            event.preventDefault()
            colorMode.value = 'dark'
          },
        },
      ],
    },
  ],
  [
    {
      label: 'Log out',
      icon: 'i-lucide-log-out',
      onSelect: logout,
    },
  ],
])
</script>

<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{
      content: collapsed ? 'w-56' : 'w-(--reka-dropdown-menu-trigger-width)',
    }"
  >
    <UButton
      v-bind="{
        label: collapsed ? undefined : user.name,
        avatar,
        trailingIcon: collapsed ? undefined : 'i-lucide-chevrons-up-down',
      }"
      color="neutral"
      variant="ghost"
      block
      :square="collapsed"
      class="data-[state=open]:bg-elevated"
      :ui="{ trailingIcon: 'text-dimmed' }"
    />
  </UDropdownMenu>
</template>
